/**
 * Look before you move (3.2): the popover a single click on a visit card opens,
 * on My week and on My day. Five lines and nothing else — who and when, the prior visit of the
 * clinician's discipline with the gap to this one, the plan of care, where the
 * 30-day period ends, and every discipline's visits in the start-of-care week
 * this visit belongs to (MY-WEEK-INSPECT-PROMPT.md, stage 1).
 *
 * A **popper**, not a modal popover: a modal one lays a backdrop over the week,
 * and the presenter must still be able to pick up any card — which closes this.
 * A mouse-down anywhere outside it or its card, or Escape, closes it too. Looking
 * writes nothing.
 */
import { useEffect, useMemo, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Paper, Popper, Stack, Typography } from "@mui/material";
import { formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import type { WeekCard } from "../../engine/myWeek";
import { selectVisitInspection, type InspectPill, type VisitInspection } from "../../engine/visitInspection";
import { visitTypeLabel } from "../../engine/roster";
import { useNow } from "../../components/useNow";
import { useDemoState } from "../../store/useDemoStore";
import type { DateKey } from "@caredna-marlin/utils/homeHealthDemo/state";
import { DONE, RESERVED, SCHEDULED } from "../episode/visitStatusColors";

/** The cards a click inspects: a visit, and a reservation that stands for one. Blocks and holds get nothing. */
export function isInspectable(card: WeekCard): boolean {
  return (card.kind === "visit" && card.realVisitId !== undefined) || ((card.kind === "planned" || card.kind === "provisional") && card.planVisitId !== undefined);
}

const dayLabel = (date: DateKey, pattern = "EEE MMM d") => formatInBranchZone(new Date(`${date}T12:00:00Z`), "UTC", pattern);

const PILL_STYLE = { done: DONE, scheduled: SCHEDULED, reserved: RESERVED } as const;

function Pill({ label, state, selected, unavailable }: { label: string; state: InspectPill["state"]; selected?: boolean; unavailable?: boolean }) {
  return (
    <Box
      component="span"
      data-pill-state={state}
      data-pill-selected={selected ? "true" : undefined}
      data-pill-unavailable={unavailable ? "true" : undefined}
      sx={{
        display: "inline-block",
        px: 0.5,
        borderRadius: 0.5,
        fontSize: 11,
        lineHeight: 1.6,
        whiteSpace: "nowrap",
        ...PILL_STYLE[state],
        // A day the patient is not available takes the warning colour as an edge,
        // not a fill: this theme's warning is a coral that, as a fill, reads as
        // the product's own canceled colour (Pete, 2026-10-08).
        ...(unavailable ? { border: "2px solid", borderColor: "warning.main" } : {}),
        ...(selected ? { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 } : {}),
      }}
    >
      {label}
    </Box>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" color="text.secondary" sx={{ width: 76, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, fontSize: 13 }}>{children}</Box>
    </Stack>
  );
}

function PriorLine({ prior }: { prior: VisitInspection["prior"] }) {
  if (prior.kind !== "found") return <>{prior.group ? `${prior.group} · ` : ""}{prior.kind === "noneYet" ? "none yet" : "none in view"}</>;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <span>{prior.group} ·</span>
      <Pill label={dayLabel(prior.date)} state={prior.state} />
      <span>
        · {prior.gapDays} {prior.gapDays === 1 ? "day" : "days"}
      </span>
    </Stack>
  );
}

/**
 * The plan line is the clinician's own discipline and nothing else (Pete,
 * 2026-10-08): she is deciding whether her own visit can move, and only her
 * discipline's plan bears on that. The other disciplines are on the week's pills,
 * and all of them are on the patient page behind the name. The day count stays
 * here, because the 60 is the plan's clock; the 30 is Period end's. A discipline
 * whose orders are not in yet has no plan to count against — its evaluation
 * alone would read "1 of 1" — so it says so.
 */
function planText(plan: NonNullable<VisitInspection["plan"]>, group: VisitInspection["group"]): string | undefined {
  const mine = plan.groups.find((g) => g.group === group);
  if (!mine) return undefined;
  if (mine.pending) return "to follow";
  return [`Visit ${mine.delivered} of ${mine.ordered}${mine.behind ? " · behind" : ""}`, `day ${plan.dayOfCert} of ${plan.certPeriodDays}`].join(" · ");
}

export function VisitInspectPopover({ card, anchorEl, clinicianName, onClose }: { card: WeekCard; anchorEl: HTMLElement; clinicianName: string; onClose: () => void }) {
  const state = useDemoState();
  const now = useNow(30_000);
  const paperRef = useRef<HTMLDivElement>(null);
  const inspection = useMemo(
    () => selectVisitInspection(state, card.realVisitId !== undefined ? { realVisitId: card.realVisitId } : { planVisitId: card.planVisitId! }, now),
    [state, card.realVisitId, card.planVisitId, now],
  );

  // Escape, or a press anywhere that is neither this popper nor its own card.
  // The card is left alone so its own click can toggle the popover shut. Escape is
  // taken on the way down and stopped, so on My day it closes this and not the
  // day's dialog underneath.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onClose();
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (paperRef.current?.contains(target) || anchorEl.contains(target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("mousedown", onDown);
    };
  }, [anchorEl, onClose]);

  const plan = card.planVisitId ? state.planVisits[card.planVisitId] : undefined;
  const typeLabel = card.kind === "visit" ? card.subtitle : plan ? visitTypeLabel(state.config, plan.visitTypeKind) : card.title;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="right-start"
      modifiers={[
        { name: "flip", options: { fallbackPlacements: ["left-start", "bottom-start", "top-start"] } },
        { name: "preventOverflow", options: { padding: 8 } },
        { name: "offset", options: { offset: [0, 6] } },
      ]}
      sx={{ zIndex: (theme) => theme.zIndex.modal }}
    >
      <Paper ref={paperRef} elevation={8} data-testid="visit-inspect" sx={{ p: 1.5, width: 340 }}>
        <Stack spacing={0.75}>
          <Box>
            <Typography variant="subtitle2" component="div">
              {inspection?.patientId !== undefined ? (
                <Link component={RouterLink} to={`/patients/${inspection.patientId}`} target="_blank" rel="noopener noreferrer">
                  {card.kind === "visit" ? card.title : (state.patients[inspection.patientRef ?? ""]?.displayName ?? card.subtitle ?? card.title)}
                </Link>
              ) : (
                card.title
              )}
            </Typography>
            {/* The day and the arrival window are already on the card beside it (Pete, 2026-10-08). */}
            <Typography variant="caption" color="text.secondary" component="div">
              {[typeLabel, clinicianName].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
          {inspection && (
            <>
              <Line label="Prior visit">
                <PriorLine prior={inspection.prior} />
              </Line>
              {inspection.plan && planText(inspection.plan, inspection.group) && (
                <Line label={`${inspection.group} plan`}>{planText(inspection.plan, inspection.group)}</Line>
              )}
              {inspection.periodEnd && (
                <Line label="Period end">
                  {dayLabel(inspection.periodEnd.date, "MMM d")}
                  {inspection.periodEnd.soon ? ` · ${inspection.periodEnd.soon}` : ""}
                </Line>
              )}
              {inspection.week && (
                <Box>
                  <Typography variant="caption" color="text.secondary" component="div">
                    Week {inspection.week.index} · {dayLabel(inspection.week.start)} – {dayLabel(inspection.week.end)}
                    {/* The order, so two pills under the week read as right or short (Pete, 2026-10-08). */}
                    {inspection.week.ordered !== undefined && inspection.group ? ` · ${inspection.week.ordered} ${inspection.group} ordered` : ""}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {inspection.week.rows.map((row) => (
                      <Stack key={row.group} direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="caption" sx={{ width: 32, flexShrink: 0, fontWeight: 600 }}>
                          {row.group}
                        </Typography>
                        {row.pills.map((pill) => (
                          <Pill
                            key={pill.key}
                            label={`${pill.prefix ? `${pill.prefix} ` : ""}${dayLabel(pill.date, "EEE d")}`}
                            state={pill.state}
                            selected={pill.selected}
                            unavailable={pill.unavailable}
                          />
                        ))}
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          )}
        </Stack>
      </Paper>
    </Popper>
  );
}
